import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import SEOHead from '../../components/seo/SEOHead';
import SchemaOrg from '../../components/seo/SchemaOrg';
import FeedbackBanner from '../../components/feature/FeedbackBanner';
import Footer from '../../components/feature/Footer';

export default function ArticleContadorIA() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEOHead 
        title="O Papel do Contador na Era da Inteligência Artificial - Ativo News"
        description="Descubra como a inteligência artificial está transformando a profissão contábil e quais habilidades os contadores precisam desenvolver para se destacar no mercado."
        keywords="contador, inteligência artificial, automação contábil, futuro da contabilidade"
        canonical="https://ativonews.com.br/artigo/papel-contador-era-inteligencia-artificial"
      />
      <SchemaOrg type="Article" data={{
        headline: "O Papel do Contador na Era da Inteligência Artificial",
        description: "Descubra como a inteligência artificial está transformando a profissão contábil e quais habilidades os contadores precisam desenvolver para se destacar no mercado.",
        author: "Dr. Ricardo Mendes",
        datePublished: "2025-01-15",
        image: "https://public.readdy.ai/ai/img_res/529f5f46dd3db16fd1e07af409c3ea1d.jpg"
      }} />
      
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-[#F8FAFC] to-white py-16 border-b border-gray-100">
          <div className="max-w-4xl mx-auto px-6">
            <Link 
              to="/categoria/tecnologia"
              className="inline-block px-4 py-2 bg-[#059669] text-white text-sm font-semibold rounded-lg mb-6 hover:bg-[#047857] transition-colors uppercase tracking-wide"
            >
              Tecnologia
            </Link>
            
            <h1 className="text-4xl lg:text-5xl font-bold text-[#0F172A] leading-tight mb-6">
              O Papel do Contador na Era da Inteligência Artificial: Adaptação ou Extinção?
            </h1>
            
            <div className="flex items-center gap-6 text-[#64748B] text-sm mb-8">
              <div className="flex items-center gap-2">
                <i className="ri-user-line text-lg"></i>
                <span className="font-medium">Dra. Camila Rodrigues</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-calendar-line text-lg"></i>
                <span>20 de Janeiro de 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-time-line text-lg"></i>
                <span>14 min de leitura</span>
              </div>
            </div>

            <div className="bg-[#D1FAE5] border-l-4 border-[#059669] p-6 rounded-r-lg">
              <p className="text-[#065F46] leading-relaxed">
                <strong className="font-bold">TL;DR:</strong> A Inteligência Artificial já chegou ao dia a dia dos escritórios contábeis — e não como uma promessa distante. Ela automatiza rotinas, reduz retrabalho, melhora a qualidade do dado e muda radicalmente o "como" o contador entrega valor. Esse movimento não elimina a profissão; ele reposiciona o contador: de operador de tarefas para gestor de risco, intérprete de normas, consultor estratégico e guardião da confiabilidade da informação. Este guia apresenta o novo papel do contador, casos práticos de uso de IA, riscos e competências essenciais para prosperar nesta nova era.
              </p>
            </div>
          </div>
        </div>

        {/* Article Content */}
        <div className="max-w-4xl mx-auto px-6 mt-12">
          {/* Sumário */}
          <div className="bg-[#F8FAFC] border border-gray-200 rounded-xl p-8 mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-6 flex items-center gap-3">
              <i className="ri-list-unordered text-[#059669]"></i>
              Sumário
            </h2>
            <nav className="space-y-3">
              <a href="#mudancas" className="block text-[#059669] hover:text-[#047857] font-medium transition-colors">
                1. O que mudou com a IA na contabilidade
              </a>
              <a href="#novo-papel" className="block text-[#059669] hover:text-[#047857] font-medium transition-colors">
                2. O novo papel do contador: de executor a estrategista
              </a>
              <a href="#ia-faz-bem" className="block text-[#059669] hover:text-[#047857] font-medium transition-colors">
                3. O que a IA faz bem (e o que ela não faz)
              </a>
              <a href="#casos-uso" className="block text-[#059669] hover:text-[#047857] font-medium transition-colors">
                4. Principais casos de uso de IA para contabilidade (na prática)
              </a>
              <a href="#riscos-etica" className="block text-[#059669] hover:text-[#047857] font-medium transition-colors">
                5. Riscos, ética e responsabilidade: por que o contador fica mais importante
              </a>
              <a href="#competencias" className="block text-[#059669] hover:text-[#047857] font-medium transition-colors">
                6. Competências do contador na era da IA
              </a>
              <a href="#implementacao" className="block text-[#059669] hover:text-[#047857] font-medium transition-colors">
                7. Como implementar IA no escritório contábil: roteiro de 30-60-90 dias
              </a>
              <a href="#faq" className="block text-[#059669] hover:text-[#047857] font-medium transition-colors">
                8. FAQ: perguntas que clientes fazem sobre IA e contabilidade
              </a>
            </nav>
          </div>

          {/* Section 1 */}
          <section id="mudancas" className="mb-16 scroll-mt-24">
            <h2 className="text-3xl font-bold text-[#0F172A] mb-6 pb-3 border-b-2 border-[#059669]">
              O que mudou com a IA na contabilidade
            </h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-[#475569] leading-relaxed mb-6">
                A contabilidade sempre foi uma disciplina baseada em <strong className="text-[#0F172A]">regras, evidências e rastreabilidade</strong>. A IA acelera isso porque aprende padrões, identifica anomalias e automatiza classificação, conciliações e checagens em escala. Em outras palavras: a tecnologia assume o "trabalho repetitivo" e abre espaço para o contador trabalhar em algo historicamente escasso: <strong className="text-[#0F172A]">interpretação, julgamento profissional e orientação estratégica</strong>.
              </p>
              <p className="text-[#475569] leading-relaxed mb-6">
                Também cresce o uso combinado de IA com automação (RPA) para viabilizar <strong className="text-[#0F172A]">"auditoria contínua"</strong> e controles em tempo real — um movimento que transforma a forma como escritórios contábeis operam e entregam valor aos clientes.
              </p>

              <div className="bg-gradient-to-br from-[#D1FAE5] to-white border-2 border-[#059669] rounded-xl p-8 my-8">
                <h3 className="text-xl font-bold text-[#0F172A] mb-4 flex items-center gap-3">
                  <i className="ri-lightbulb-line text-[#059669] text-2xl"></i>
                  Principais mudanças trazidas pela IA
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-[#475569]">
                    <i className="ri-checkbox-circle-fill text-[#059669] text-xl mt-1 flex-shrink-0"></i>
                    <span><strong className="text-[#0F172A]">Automação de tarefas repetitivas:</strong> Classificação de documentos, lançamentos contábeis e conciliações bancárias</span>
                  </li>
                  <li className="flex items-start gap-3 text-[#475569]">
                    <i className="ri-checkbox-circle-fill text-[#059669] text-xl mt-1 flex-shrink-0"></i>
                    <span><strong className="text-[#0F172A]">Detecção de anomalias:</strong> Identificação automática de erros, fraudes e inconsistências</span>
                  </li>
                  <li className="flex items-start gap-3 text-[#475569]">
                    <i className="ri-checkbox-circle-fill text-[#059669] text-xl mt-1 flex-shrink-0"></i>
                    <span><strong className="text-[#0F172A]">Análise preditiva:</strong> Previsão de fluxo de caixa, inadimplência e tendências financeiras</span>
                  </li>
                  <li className="flex items-start gap-3 text-[#475569]">
                    <i className="ri-checkbox-circle-fill text-[#059669] text-xl mt-1 flex-shrink-0"></i>
                    <span><strong className="text-[#0F172A]">Relatórios inteligentes:</strong> Geração automática de análises e insights para tomada de decisão</span>
                  </li>
                  <li className="flex items-start gap-3 text-[#475569]">
                    <i className="ri-checkbox-circle-fill text-[#059669] text-xl mt-1 flex-shrink-0"></i>
                    <span><strong className="text-[#0F172A]">Atendimento 24/7:</strong> Chatbots e assistentes virtuais para dúvidas frequentes</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 2 */}
          <section id="novo-papel" className="mb-16 scroll-mt-24">
            <h2 className="text-3xl font-bold text-[#0F172A] mb-6 pb-3 border-b-2 border-[#059669]">
              O novo papel do contador: de executor a estrategista
            </h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-[#475569] leading-relaxed mb-6">
                O contador em tempos de IA passa a ser valorizado por três capacidades centrais:
              </p>

              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gradient-to-br from-[#DBEAFE] to-white border-2 border-[#1E40AF] rounded-xl p-6">
                  <div className="w-14 h-14 bg-[#1E40AF] text-white rounded-xl flex items-center justify-center mx-auto mb-4">
                    <i className="ri-shield-check-line text-3xl"></i>
                  </div>
                  <h3 className="text-lg font-bold text-[#0F172A] mb-3 text-center">
                    Garantia de Confiabilidade e Compliance
                  </h3>
                  <p className="text-sm text-[#475569] text-center leading-relaxed">
                    A IA pode sugerir, mas quem responde por obrigações, conformidade e coerência é o profissional que entende contexto, legislação, materialidade e risco.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-[#FEF3C7] to-white border-2 border-[#F59E0B] rounded-xl p-6">
                  <div className="w-14 h-14 bg-[#F59E0B] text-white rounded-xl flex items-center justify-center mx-auto mb-4">
                    <i className="ri-line-chart-line text-3xl"></i>
                  </div>
                  <h3 className="text-lg font-bold text-[#0F172A] mb-3 text-center">
                    Leitura de Negócio (Contabilidade Consultiva)
                  </h3>
                  <p className="text-sm text-[#475569] text-center leading-relaxed">
                    Com rotinas automatizadas, o contador ganha tempo para "traduzir números em decisões": margem, fluxo de caixa, precificação, planejamento tributário e indicadores.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-[#FEE2E2] to-white border-2 border-[#DC2626] rounded-xl p-6">
                  <div className="w-14 h-14 bg-[#DC2626] text-white rounded-xl flex items-center justify-center mx-auto mb-4">
                    <i className="ri-alert-line text-3xl"></i>
                  </div>
                  <h3 className="text-lg font-bold text-[#0F172A] mb-3 text-center">
                    Gestão de Riscos e Controles
                  </h3>
                  <p className="text-sm text-[#475569] text-center leading-relaxed">
                    Em vez de conferir amostras, o contador passa a desenhar controles que monitoram 100% das transações (alertas, trilhas e exceções).
                  </p>
                </div>
              </div>

              <div className="bg-[#D1FAE5] border-l-4 border-[#059669] p-6 rounded-r-lg my-8">
                <p className="text-[#065F46] leading-relaxed">
                  <strong className="font-bold">💡 Insight:</strong> Essa visão de reconfiguração do papel (mais estratégico e consultivo) aparece com frequência em conteúdos sobre tendências da contabilidade impulsionadas por automação e IA. O contador deixa de ser um "digitador de dados" para se tornar um "intérprete de informações".
                </p>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section id="ia-faz-bem" className="mb-16 scroll-mt-24">
            <h2 className="text-3xl font-bold text-[#0F172A] mb-6 pb-3 border-b-2 border-[#059669]">
              O que a IA faz bem (e o que ela não faz)
            </h2>
            <div className="prose prose-lg max-w-none">
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                {/* O que a IA faz bem */}
                <div className="bg-gradient-to-br from-[#D1FAE5] to-white border-2 border-[#059669] rounded-xl p-6">
                  <h3 className="text-xl font-bold text-[#0F172A] mb-4 flex items-center gap-3">
                    <i className="ri-checkbox-circle-line text-[#059669] text-2xl"></i>
                    A IA faz bem
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3 text-[#475569]">
                      <i className="ri-arrow-right-circle-fill text-[#059669] text-lg mt-1 flex-shrink-0"></i>
                      <span>Classificar documentos e lançamentos com base em padrões históricos</span>
                    </li>
                    <li className="flex items-start gap-3 text-[#475569]">
                      <i className="ri-arrow-right-circle-fill text-[#059669] text-lg mt-1 flex-shrink-0"></i>
                      <span>Extrair dados de notas, faturas e PDFs (OCR + modelos)</span>
                    </li>
                    <li className="flex items-start gap-3 text-[#475569]">
                      <i className="ri-arrow-right-circle-fill text-[#059669] text-lg mt-1 flex-shrink-0"></i>
                      <span>Identificar inconsistências (valores fora do padrão, duplicidades, fraudes)</span>
                    </li>
                    <li className="flex items-start gap-3 text-[#475569]">
                      <i className="ri-arrow-right-circle-fill text-[#059669] text-lg mt-1 flex-shrink-0"></i>
                      <span>Ajudar na elaboração de relatórios e narrativas (análises e insights)</span>
                    </li>
                    <li className="flex items-start gap-3 text-[#475569]">
                      <i className="ri-arrow-right-circle-fill text-[#059669] text-lg mt-1 flex-shrink-0"></i>
                      <span>Acelerar fechamento contábil com conciliações automatizadas</span>
                    </li>
                  </ul>
                </div>

                {/* O que a IA não faz */}
                <div className="bg-gradient-to-br from-[#FEE2E2] to-white border-2 border-[#DC2626] rounded-xl p-6">
                  <h3 className="text-xl font-bold text-[#0F172A] mb-4 flex items-center gap-3">
                    <i className="ri-close-circle-line text-[#DC2626] text-2xl"></i>
                    A IA não faz (sozinha, com segurança)
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3 text-[#475569]">
                      <i className="ri-close-circle-fill text-[#DC2626] text-lg mt-1 flex-shrink-0"></i>
                      <span>Assumir responsabilidade técnica e legal</span>
                    </li>
                    <li className="flex items-start gap-3 text-[#475569]">
                      <i className="ri-close-circle-fill text-[#DC2626] text-lg mt-1 flex-shrink-0"></i>
                      <span>Interpretar exceções complexas sem orientação</span>
                    </li>
                    <li className="flex items-start gap-3 text-[#475569]">
                      <i className="ri-close-circle-fill text-[#DC2626] text-lg mt-1 flex-shrink-0"></i>
                      <span>Garantir conformidade quando o dado de entrada é ruim ("garbage in, garbage out")</span>
                    </li>
                    <li className="flex items-start gap-3 text-[#475569]">
                      <i className="ri-close-circle-fill text-[#DC2626] text-lg mt-1 flex-shrink-0"></i>
                      <span>Substituir julgamento profissional em situações ambíguas (normas, contratos, contingências)</span>
                    </li>
                    <li className="flex items-start gap-3 text-[#475569]">
                      <i className="ri-close-circle-fill text-[#DC2626] text-lg mt-1 flex-shrink-0"></i>
                      <span>Entender contexto de negócio e particularidades do cliente</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-[#DBEAFE] border-l-4 border-[#1E40AF] p-6 rounded-r-lg">
                <p className="text-[#1E3A8A] leading-relaxed">
                  <strong className="font-bold">🎯 Conclusão:</strong> Por isso, o contador vira o "piloto" e a IA vira o "copiloto". A tecnologia potencializa, mas não substitui o julgamento profissional, a responsabilidade técnica e a capacidade de interpretar contexto.
                </p>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section id="casos-uso" className="mb-16 scroll-mt-24">
            <h2 className="text-3xl font-bold text-[#0F172A] mb-6 pb-3 border-b-2 border-[#059669]">
              Principais casos de uso de IA para contabilidade (na prática)
            </h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-[#475569] leading-relaxed mb-6">
                Abaixo, os usos mais comuns (e onde o retorno costuma ser mais rápido):
              </p>

              <div className="space-y-6">
                {/* Caso 1 */}
                <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#059669] text-white rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-file-list-3-line text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[#0F172A] mb-3">
                        1) Automação de lançamentos e classificação contábil
                      </h3>
                      <p className="text-[#475569] leading-relaxed mb-3">
                        IA sugere classificação e centro de custo com base em histórico. O contador cria regras, valida exceções e monitora qualidade.
                      </p>
                      <div className="bg-[#F8FAFC] p-4 rounded-lg">
                        <p className="text-sm text-[#475569]">
                          <strong className="text-[#0F172A]">Benefício:</strong> Redução de 60-80% do tempo de classificação manual. Maior consistência e menos erros humanos.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Caso 2 */}
                <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#1E40AF] text-white rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-bank-line text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[#0F172A] mb-3">
                        2) Conciliação bancária e contábil
                      </h3>
                      <p className="text-[#475569] leading-relaxed mb-3">
                        Ferramentas de IA aceleram matching e sinalizam divergências, reduzindo horas operacionais.
                      </p>
                      <div className="bg-[#F8FAFC] p-4 rounded-lg">
                        <p className="text-sm text-[#475569]">
                          <strong className="text-[#0F172A]">Benefício:</strong> Conciliações que levavam dias agora são feitas em minutos. Identificação automática de pendências.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Caso 3 */}
                <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#7C3AED] text-white rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-search-eye-line text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[#0F172A] mb-3">
                        3) Auditoria contínua e detecção de anomalias
                      </h3>
                      <p className="text-[#475569] leading-relaxed mb-3">
                        IA identifica padrões anormais e transações fora do esperado, fortalecendo compliance e mitigando fraude.
                      </p>
                      <div className="bg-[#F8FAFC] p-4 rounded-lg">
                        <p className="text-sm text-[#475569]">
                          <strong className="text-[#0F172A]">Benefício:</strong> Monitoramento 24/7 de 100% das transações. Alertas em tempo real para situações de risco.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Caso 4 */}
                <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#F59E0B] text-white rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-bar-chart-box-line text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[#0F172A] mb-3">
                        4) Relatórios gerenciais e "narrativas" para decisão
                      </h3>
                      <p className="text-[#475569] leading-relaxed mb-3">
                        A IA ajuda a resumir variações, explicar desvios e preparar análises — mas o contador valida premissas e contexto.
                      </p>
                      <div className="bg-[#F8FAFC] p-4 rounded-lg">
                        <p className="text-sm text-[#475569]">
                          <strong className="text-[#0F172A]">Benefício:</strong> Relatórios mais ricos e insights acionáveis. Contador foca em interpretação, não em formatação.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Caso 5 */}
                <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#DC2626] text-white rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-customer-service-2-line text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[#0F172A] mb-3">
                        5) Atendimento e suporte ao cliente (com base de conhecimento)
                      </h3>
                      <p className="text-[#475569] leading-relaxed mb-3">
                        Chatbots internos treinados com procedimentos e rotinas do escritório respondem dúvidas padrão e abrem tickets com dados completos.
                      </p>
                      <div className="bg-[#F8FAFC] p-4 rounded-lg">
                        <p className="text-sm text-[#475569]">
                          <strong className="text-[#0F172A]">Benefício:</strong> Atendimento 24/7, redução de chamados repetitivos e mais tempo para questões complexas.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 5 */}
          <section id="riscos-etica" className="mb-16 scroll-mt-24">
            <h2 className="text-3xl font-bold text-[#0F172A] mb-6 pb-3 border-b-2 border-[#059669]">
              Riscos, ética e responsabilidade: por que o contador fica mais importante
            </h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-[#475569] leading-relaxed mb-6">
                Quanto mais automação, maior o risco de erro em escala. Um modelo mal parametrizado pode classificar errado centenas de transações, e uma integração ruim pode duplicar lançamentos. Por isso, a <strong className="text-[#0F172A]">governança passa a ser parte do trabalho contábil</strong>.
              </p>

              <h3 className="text-2xl font-bold text-[#0F172A] mt-10 mb-4">
                Principais riscos
              </h3>

              <div className="space-y-4 mb-8">
                <div className="border-l-4 border-[#DC2626] bg-[#FEE2E2] p-5 rounded-r-lg">
                  <h4 className="font-bold text-[#0F172A] mb-2 flex items-center gap-2">
                    <i className="ri-error-warning-line text-[#DC2626]"></i>
                    Alucinação (IA afirmar algo incorreto)
                  </h4>
                  <p className="text-[#475569] text-sm">
                    Modelos de linguagem podem gerar informações plausíveis mas incorretas. Validação humana é essencial.
                  </p>
                </div>

                <div className="border-l-4 border-[#DC2626] bg-[#FEE2E2] p-5 rounded-r-lg">
                  <h4 className="font-bold text-[#0F172A] mb-2 flex items-center gap-2">
                    <i className="ri-lock-unlock-line text-[#DC2626]"></i>
                    Vazamento de dados (informações fiscais/financeiras sensíveis)
                  </h4>
                  <p className="text-[#475569] text-sm">
                    Dados contábeis são confidenciais. Uso de IA exige controles rigorosos de segurança e LGPD.
                  </p>
                </div>

                <div className="border-l-4 border-[#DC2626] bg-[#FEE2E2] p-5 rounded-r-lg">
                  <h4 className="font-bold text-[#0F172A] mb-2 flex items-center gap-2">
                    <i className="ri-scales-line text-[#DC2626]"></i>
                    Viés em classificações e decisões
                  </h4>
                  <p className="text-[#475569] text-sm">
                    IA aprende com dados históricos. Se os dados têm viés, o modelo reproduz e amplifica o problema.
                  </p>
                </div>

                <div className="border-l-4 border-[#DC2626] bg-[#FEE2E2] p-5 rounded-r-lg">
                  <h4 className="font-bold text-[#0F172A] mb-2 flex items-center gap-2">
                    <i className="ri-file-damage-line text-[#DC2626]"></i>
                    Dependência excessiva sem validação
                  </h4>
                  <p className="text-[#475569] text-sm">
                    Confiar cegamente em sugestões da IA sem revisão crítica pode gerar erros em escala.
                  </p>
                </div>

                <div className="border-l-4 border-[#DC2626] bg-[#FEE2E2] p-5 rounded-r-lg">
                  <h4 className="font-bold text-[#0F172A] mb-2 flex items-center gap-2">
                    <i className="ri-shield-cross-line text-[#DC2626]"></i>
                    Falta de transparência (caixa-preta)
                  </h4>
                  <p className="text-[#475569] text-sm">
                    Alguns modelos de IA não explicam como chegaram a uma conclusão, dificultando auditoria e compliance.
                  </p>
                </div>
              </div>

              <h3 className="text-2xl font-bold text-[#0F172A] mt-10 mb-4">
                O papel do contador como guardião da confiabilidade
              </h3>

              <p className="text-[#475569] leading-relaxed mb-4">
                Por isso, o contador se torna ainda mais importante:
              </p>

              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3 text-[#475569]">
                  <i className="ri-shield-check-line text-[#059669] text-xl mt-1 flex-shrink-0"></i>
                  <span><strong className="text-[#0F172A]">Validação de outputs:</strong> Revisar e questionar sugestões da IA antes de aplicar</span>
                </li>
                <li className="flex items-start gap-3 text-[#475569]">
                  <i className="ri-shield-check-line text-[#059669] text-xl mt-1 flex-shrink-0"></i>
                  <span><strong className="text-[#0F172A]">Governança de dados:</strong> Garantir qualidade, segurança e conformidade dos dados usados pela IA</span>
                </li>
                <li className="flex items-start gap-3 text-[#475569]">
                  <i className="ri-shield-check-line text-[#059669] text-xl mt-1 flex-shrink-0"></i>
                  <span><strong className="text-[#0F172A]">Responsabilidade técnica:</strong> Assumir a assinatura e responder legalmente pelas informações</span>
                </li>
                <li className="flex items-start gap-3 text-[#475569]">
                  <i className="ri-shield-check-line text-[#059669] text-xl mt-1 flex-shrink-0"></i>
                  <span><strong className="text-[#0F172A]">Interpretação de contexto:</strong> Entender particularidades do negócio que a IA não captura</span>
                </li>
                <li className="flex items-start gap-3 text-[#475569]">
                  <i className="ri-shield-check-line text-[#059669] text-xl mt-1 flex-shrink-0"></i>
                  <span><strong className="text-[#0F172A]">Ética profissional:</strong> Garantir que o uso de IA respeita normas, privacidade e transparência</span>
                </li>
              </ul>

              <div className="bg-[#FEF3C7] border-l-4 border-[#F59E0B] p-6 rounded-r-lg">
                <p className="text-[#92400E] leading-relaxed">
                  <strong className="font-bold">⚠️ Importante:</strong> A IA é uma ferramenta poderosa, mas não substitui o julgamento profissional, a responsabilidade técnica e a capacidade de interpretar contexto. O contador que domina IA se torna mais valioso, não menos.
                </p>
              </div>
            </div>
          </section>

          {/* Section 6 */}
          <section id="competencias" className="mb-16 scroll-mt-24">
            <h2 className="text-3xl font-bold text-[#0F172A] mb-6 pb-3 border-b-2 border-[#059669]">
              Competências do contador na era da IA
            </h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-[#475569] leading-relaxed mb-6">
                Para prosperar na era da IA, o contador precisa desenvolver um conjunto de competências que vão além do conhecimento técnico tradicional:
              </p>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                {/* Competências Técnicas */}
                <div className="bg-gradient-to-br from-[#DBEAFE] to-white border-2 border-[#1E40AF] rounded-xl p-6">
                  <h3 className="text-xl font-bold text-[#0F172A] mb-4 flex items-center gap-3">
                    <i className="ri-code-box-line text-[#1E40AF] text-2xl"></i>
                    Competências Técnicas
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3 text-[#475569]">
                      <i className="ri-checkbox-circle-fill text-[#1E40AF] text-lg mt-1 flex-shrink-0"></i>
                      <span><strong className="text-[#0F172A]">Alfabetização em dados:</strong> Entender como dados são coletados, tratados e analisados</span>
                    </li>
                    <li className="flex items-start gap-3 text-[#475569]">
                      <i className="ri-checkbox-circle-fill text-[#1E40AF] text-lg mt-1 flex-shrink-0"></i>
                      <span><strong className="text-[#0F172A]">Noções de IA e automação:</strong> Saber o que é possível automatizar e como</span>
                    </li>
                    <li className="flex items-start gap-3 text-[#475569]">
                      <i className="ri-checkbox-circle-fill text-[#1E40AF] text-lg mt-1 flex-shrink-0"></i>
                      <span><strong className="text-[#0F172A]">Integração de sistemas:</strong> Conectar ERPs, bancos e ferramentas de IA</span>
                    </li>
                    <li className="flex items-start gap-3 text-[#475569]">
                      <i className="ri-checkbox-circle-fill text-[#1E40AF] text-lg mt-1 flex-shrink-0"></i>
                      <span><strong className="text-[#0F172A]">Análise crítica de outputs:</strong> Validar e questionar resultados da IA</span>
                    </li>
                  </ul>
                </div>

                {/* Competências Comportamentais */}
                <div className="bg-gradient-to-br from-[#FEF3C7] to-white border-2 border-[#F59E0B] rounded-xl p-6">
                  <h3 className="text-xl font-bold text-[#0F172A] mb-4 flex items-center gap-3">
                    <i className="ri-team-line text-[#F59E0B] text-2xl"></i>
                    Competências Comportamentais
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3 text-[#475569]">
                      <i className="ri-checkbox-circle-fill text-[#F59E0B] text-lg mt-1 flex-shrink-0"></i>
                      <span><strong className="text-[#0F172A]">Pensamento crítico:</strong> Questionar premissas e validar conclusões</span>
                    </li>
                    <li className="flex items-start gap-3 text-[#475569]">
                      <i className="ri-checkbox-circle-fill text-[#F59E0B] text-lg mt-1 flex-shrink-0"></i>
                      <span><strong className="text-[#0F172A]">Comunicação estratégica:</strong> Traduzir números em insights acionáveis</span>
                    </li>
                    <li className="flex items-start gap-3 text-[#475569]">
                      <i className="ri-checkbox-circle-fill text-[#F59E0B] text-lg mt-1 flex-shrink-0"></i>
                      <span><strong className="text-[#0F172A]">Adaptabilidade:</strong> Aprender continuamente e se ajustar a novas tecnologias</span>
                    </li>
                    <li className="flex items-start gap-3 text-[#475569]">
                      <i className="ri-checkbox-circle-fill text-[#F59E0B] text-lg mt-1 flex-shrink-0"></i>
                      <span><strong className="text-[#0F172A]">Visão de negócio:</strong> Entender o contexto e as necessidades do cliente</span>
                    </li>
                  </ul>
                </div>

                {/* Competências Estratégicas */}
                <div className="bg-gradient-to-br from-[#D1FAE5] to-white border-2 border-[#059669] rounded-xl p-6">
                  <h3 className="text-xl font-bold text-[#0F172A] mb-4 flex items-center gap-3">
                    <i className="ri-lightbulb-line text-[#059669] text-2xl"></i>
                    Competências Estratégicas
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3 text-[#475569]">
                      <i className="ri-checkbox-circle-fill text-[#059669] text-lg mt-1 flex-shrink-0"></i>
                      <span><strong className="text-[#0F172A]">Consultoria de negócios:</strong> Orientar decisões estratégicas com base em dados</span>
                    </li>
                    <li className="flex items-start gap-3 text-[#475569]">
                      <i className="ri-checkbox-circle-fill text-[#059669] text-lg mt-1 flex-shrink-0"></i>
                      <span><strong className="text-[#0F172A]">Gestão de riscos:</strong> Identificar e mitigar riscos financeiros e operacionais</span>
                    </li>
                    <li className="flex items-start gap-3 text-[#475569]">
                      <i className="ri-checkbox-circle-fill text-[#059669] text-lg mt-1 flex-shrink-0"></i>
                      <span><strong className="text-[#0F172A]">Planejamento tributário:</strong> Otimizar carga fiscal com conformidade</span>
                    </li>
                    <li className="flex items-start gap-3 text-[#475569]">
                      <i className="ri-checkbox-circle-fill text-[#059669] text-lg mt-1 flex-shrink-0"></i>
                      <span><strong className="text-[#0F172A]">Inovação e melhoria contínua:</strong> Buscar constantemente formas de agregar mais valor</span>
                    </li>
                  </ul>
                </div>

                {/* Competências Éticas */}
                <div className="bg-gradient-to-br from-[#FEE2E2] to-white border-2 border-[#DC2626] rounded-xl p-6">
                  <h3 className="text-xl font-bold text-[#0F172A] mb-4 flex items-center gap-3">
                    <i className="ri-shield-check-line text-[#DC2626] text-2xl"></i>
                    Competências Éticas
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3 text-[#475569]">
                      <i className="ri-checkbox-circle-fill text-[#DC2626] text-lg mt-1 flex-shrink-0"></i>
                      <span><strong className="text-[#0F172A]">Responsabilidade profissional:</strong> Assumir a assinatura e responder legalmente</span>
                    </li>
                    <li className="flex items-start gap-3 text-[#475569]">
                      <i className="ri-checkbox-circle-fill text-[#DC2626] text-lg mt-1 flex-shrink-0"></i>
                      <span><strong className="text-[#0F172A]">Transparência:</strong> Explicar como decisões foram tomadas (com ou sem IA)</span>
                    </li>
                    <li className="flex items-start gap-3 text-[#475569]">
                      <i className="ri-checkbox-circle-fill text-[#DC2626] text-lg mt-1 flex-shrink-0"></i>
                      <span><strong className="text-[#0F172A]">Privacidade e segurança:</strong> Proteger dados sensíveis dos clientes</span>
                    </li>
                    <li className="flex items-start gap-3 text-[#475569]">
                      <i className="ri-checkbox-circle-fill text-[#DC2626] text-lg mt-1 flex-shrink-0"></i>
                      <span><strong className="text-[#0F172A]">Conformidade normativa:</strong> Garantir que o uso de IA respeita regulamentações</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-[#D1FAE5] border-l-4 border-[#059669] p-6 rounded-r-lg">
                <p className="text-[#065F46] leading-relaxed">
                  <strong className="font-bold">💡 Insight:</strong> O contador que desenvolve essas competências não compete com a IA — ele a utiliza como ferramenta para entregar mais valor, assumir responsabilidades maiores e se posicionar como parceiro estratégico dos clientes.
                </p>
              </div>
            </div>
          </section>

          {/* Section 7 */}
          <section id="implementacao" className="mb-16 scroll-mt-24">
            <h2 className="text-3xl font-bold text-[#0F172A] mb-6 pb-3 border-b-2 border-[#059669]">
              Como implementar IA no escritório contábil: roteiro de 30-60-90 dias
            </h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-[#475569] leading-relaxed mb-6">
                Use este roteiro prático para começar a implementar IA no seu escritório de forma estruturada e com resultados mensuráveis:
              </p>

              <div className="space-y-6">
                {/* Primeiros 30 dias */}
                <div className="bg-gradient-to-r from-[#DBEAFE] to-white border-l-4 border-[#1E40AF] rounded-r-xl p-6">
                  <h3 className="text-xl font-bold text-[#0F172A] mb-4 flex items-center gap-3">
                    <span className="w-10 h-10 bg-[#1E40AF] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">30</span>
                    Primeiros 30 dias: Diagnóstico e Preparação
                  </h3>
                  <ul className="space-y-2 ml-13">
                    <li className="flex items-start gap-2 text-[#475569]">
                      <i className="ri-checkbox-blank-circle-fill text-[#1E40AF] text-xs mt-2 flex-shrink-0"></i>
                      <span><strong className="text-[#0F172A]">Mapeie processos repetitivos:</strong> Identifique tarefas que consomem mais tempo (classificação, conciliação, relatórios)</span>
                    </li>
                    <li className="flex items-start gap-2 text-[#475569]">
                      <i className="ri-checkbox-blank-circle-fill text-[#1E40AF] text-xs mt-2 flex-shrink-0"></i>
                      <span><strong className="text-[#0F172A]">Avalie qualidade dos dados:</strong> IA precisa de dados limpos e estruturados para funcionar bem</span>
                    </li>
                    <li className="flex items-start gap-2 text-[#475569]">
                      <i className="ri-checkbox-blank-circle-fill text-[#1E40AF] text-xs mt-2 flex-shrink-0"></i>
                      <span><strong className="text-[#0F172A]">Pesquise ferramentas:</strong> Explore soluções de IA específicas para contabilidade (classificação, OCR, conciliação)</span>
                    </li>
                    <li className="flex items-start gap-2 text-[#475569]">
                      <i className="ri-checkbox-blank-circle-fill text-[#1E40AF] text-xs mt-2 flex-shrink-0"></i>
                      <span><strong className="text-[#0F172A]">Defina métricas de sucesso:</strong> Tempo economizado, redução de erros, satisfação do cliente</span>
                    </li>
                    <li className="flex items-start gap-2 text-[#475569]">
                      <i className="ri-checkbox-blank-circle-fill text-[#1E40AF] text-xs mt-2 flex-shrink-0"></i>
                      <span><strong className="text-[#0F172A]">Capacite a equipe:</strong> Treinamento básico sobre IA e suas aplicações na contabilidade</span>
                    </li>
                  </ul>
                </div>

                {/* 60 dias */}
                <div className="bg-gradient-to-r from-[#D1FAE5] to-white border-l-4 border-[#059669] rounded-r-xl p-6">
                  <h3 className="text-xl font-bold text-[#0F172A] mb-4 flex items-center gap-3">
                    <span className="w-10 h-10 bg-[#059669] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">60</span>
                    60 dias: Piloto e Validação
                  </h3>
                  <ul className="space-y-2 ml-13">
                    <li className="flex items-start gap-2 text-[#475569]">
                      <i className="ri-checkbox-blank-circle-fill text-[#059669] text-xs mt-2 flex-shrink-0"></i>
                      <span><strong className="text-[#0F172A]">Escolha um processo piloto:</strong> Comece com algo de alto impacto e baixa complexidade (ex: classificação de despesas)</span>
                    </li>
                    <li className="flex items-start gap-2 text-[#475569]">
                      <i className="ri-checkbox-blank-circle-fill text-[#059669] text-xs mt-2 flex-shrink-0"></i>
                      <span><strong className="text-[#0F172A]">Implemente a ferramenta:</strong> Configure, integre com sistemas existentes e treine o modelo</span>
                    </li>
                    <li className="flex items-start gap-2 text-[#475569]">
                      <i className="ri-checkbox-blank-circle-fill text-[#059669] text-xs mt-2 flex-shrink-0"></i>
                      <span><strong className="text-[#0F172A]">Teste em ambiente controlado:</strong> Use dados reais mas com supervisão constante</span>
                    </li>
                    <li className="flex items-start gap-2 text-[#475569]">
                      <i className="ri-checkbox-blank-circle-fill text-[#059669] text-xs mt-2 flex-shrink-0"></i>
                      <span><strong className="text-[#0F172A]">Valide resultados:</strong> Compare outputs da IA com trabalho manual, meça precisão</span>
                    </li>
                    <li className="flex items-start gap-2 text-[#475569]">
                      <i className="ri-checkbox-blank-circle-fill text-[#059669] text-xs mt-2 flex-shrink-0"></i>
                      <span><strong className="text-[#0F172A]">Ajuste e refine:</strong> Corrija erros, melhore regras e retreine o modelo</span>
                    </li>
                  </ul>
                </div>

                {/* 90 dias */}
                <div className="bg-gradient-to-r from-[#FEF3C7] to-white border-l-4 border-[#F59E0B] rounded-r-xl p-6">
                  <h3 className="text-xl font-bold text-[#0F172A] mb-4 flex items-center gap-3">
                    <span className="w-10 h-10 bg-[#F59E0B] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">90</span>
                    90 dias: Expansão e Otimização
                  </h3>
                  <ul className="space-y-2 ml-13">
                    <li className="flex items-start gap-2 text-[#475569]">
                      <i className="ri-checkbox-blank-circle-fill text-[#F59E0B] text-xs mt-2 flex-shrink-0"></i>
                      <span><strong className="text-[#0F172A]">Expanda para outros processos:</strong> Aplique IA em conciliações, relatórios e atendimento</span>
                    </li>
                    <li className="flex items-start gap-2 text-[#475569]">
                      <i className="ri-checkbox-blank-circle-fill text-[#F59E0B] text-xs mt-2 flex-shrink-0"></i>
                      <span><strong className="text-[#0F172A]">Documente procedimentos:</strong> Crie manuais de uso, regras de validação e controles</span>
                    </li>
                    <li className="flex items-start gap-2 text-[#475569]">
                      <i className="ri-checkbox-blank-circle-fill text-[#F59E0B] text-xs mt-2 flex-shrink-0"></i>
                      <span><strong className="text-[#0F172A]">Monitore KPIs:</strong> Acompanhe tempo economizado, redução de erros e satisfação</span>
                    </li>
                    <li className="flex items-start gap-2 text-[#475569]">
                      <i className="ri-checkbox-blank-circle-fill text-[#F59E0B] text-xs mt-2 flex-shrink-0"></i>
                      <span><strong className="text-[#0F172A]">Comunique resultados:</strong> Mostre aos clientes como a IA melhora a qualidade do serviço</span>
                    </li>
                    <li className="flex items-start gap-2 text-[#475569]">
                      <i className="ri-checkbox-blank-circle-fill text-[#F59E0B] text-xs mt-2 flex-shrink-0"></i>
                      <span><strong className="text-[#0F172A]">Planeje próximos passos:</strong> Identifique novas oportunidades de automação e melhoria</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-[#DBEAFE] border-l-4 border-[#1E40AF] p-6 rounded-r-lg mt-8">
                <p className="text-[#1E3A8A] leading-relaxed">
                  <strong className="font-bold">🎯 Resultado esperado:</strong> Ao final de 90 dias, você terá pelo menos um processo automatizado com IA, equipe capacitada, métricas de sucesso e um plano claro para expansão. O importante é começar pequeno, validar ROI e escalar gradualmente.
                </p>
              </div>
            </div>
          </section>

          {/* Section 8 - FAQ */}
          <section id="faq" className="mb-16 scroll-mt-24">
            <h2 className="text-3xl font-bold text-[#0F172A] mb-6 pb-3 border-b-2 border-[#059669]">
              FAQ: Perguntas que clientes fazem sobre IA e contabilidade
            </h2>
            <div className="space-y-4">
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow">
                <div className="bg-[#F8FAFC] p-5 border-b border-gray-200">
                  <h3 className="text-lg font-bold text-[#0F172A] flex items-start gap-3">
                    <i className="ri-question-line text-[#059669] text-xl flex-shrink-0 mt-1"></i>
                    <span>A IA vai substituir os contadores?</span>
                  </h3>
                </div>
                <div className="p-5">
                  <p className="text-[#475569] leading-relaxed">
                    Não. A IA automatiza tarefas repetitivas, mas não substitui julgamento profissional, responsabilidade técnica e capacidade de interpretar contexto. O contador que domina IA se torna mais valioso, assumindo papel mais estratégico e consultivo.
                  </p>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow">
                <div className="bg-[#F8FAFC] p-5 border-b border-gray-200">
                  <h3 className="text-lg font-bold text-[#0F172A] flex items-start gap-3">
                    <i className="ri-question-line text-[#059669] text-xl flex-shrink-0 mt-1"></i>
                    <span>Meus dados estão seguros com IA?</span>
                  </h3>
                </div>
                <div className="p-5">
                  <p className="text-[#475569] leading-relaxed">
                    Depende da ferramenta e dos controles implementados. Escritórios sérios usam soluções com criptografia, controles de acesso e conformidade com LGPD. Sempre pergunte ao seu contador sobre as medidas de segurança adotadas.
                  </p>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow">
                <div className="bg-[#F8FAFC] p-5 border-b border-gray-200">
                  <h3 className="text-lg font-bold text-[#0F172A] flex items-start gap-3">
                    <i className="ri-question-line text-[#059669] text-xl flex-shrink-0 mt-1"></i>
                    <span>A IA pode cometer erros?</span>
                  </h3>
                </div>
                <div className="p-5">
                  <p className="text-[#475569] leading-relaxed">
                    Sim. Por isso, contadores responsáveis sempre validam outputs da IA antes de aplicar. A IA é uma ferramenta de apoio, não uma substituta do profissional. Erros acontecem quando há dependência excessiva sem validação humana.
                  </p>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow">
                <div className="bg-[#F8FAFC] p-5 border-b border-gray-200">
                  <h3 className="text-lg font-bold text-[#0F172A] flex items-start gap-3">
                    <i className="ri-question-line text-[#059669] text-xl flex-shrink-0 mt-1"></i>
                    <span>Quanto custa implementar IA no escritório contábil?</span>
                  </h3>
                </div>
                <div className="p-5">
                  <p className="text-[#475569] leading-relaxed">
                    Varia muito. Há ferramentas gratuitas (como ChatGPT para análises pontuais) e soluções corporativas que custam milhares por mês. O importante é começar pequeno, validar ROI e escalar gradualmente. Muitas vezes, o retorno vem em meses.
                  </p>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow">
                <div className="bg-[#F8FAFC] p-5 border-b border-gray-200">
                  <h3 className="text-lg font-bold text-[#0F172A] flex items-start gap-3">
                    <i className="ri-question-line text-[#059669] text-xl flex-shrink-0 mt-1"></i>
                    <span>Como sei se meu contador está usando IA de forma responsável?</span>
                  </h3>
                </div>
                <div className="p-5">
                  <p className="text-[#475569] leading-relaxed">
                    Pergunte sobre: 1) Quais ferramentas são usadas; 2) Como os dados são protegidos; 3) Quais controles de validação existem; 4) Quem assume responsabilidade técnica. Um contador transparente responderá com clareza e confiança.
                  </p>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow">
                <div className="bg-[#F8FAFC] p-5 border-b border-gray-200">
                  <h3 className="text-lg font-bold text-[#0F172A] flex items-start gap-3">
                    <i className="ri-question-line text-[#059669] text-xl flex-shrink-0 mt-1"></i>
                    <span>Preciso entender de tecnologia para trabalhar com um contador que usa IA?</span>
                  </h3>
                </div>
                <div className="p-5">
                  <p className="text-[#475569] leading-relaxed">
                    Não. O papel do contador é justamente traduzir complexidade técnica em informações úteis para decisão. Você deve receber relatórios claros, insights acionáveis e orientações estratégicas — independente da tecnologia usada nos bastidores.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <div className="bg-gradient-to-br from-[#059669] to-[#047857] rounded-2xl p-10 text-center text-white mt-16">
            <div className="max-w-2xl mx-auto">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-robot-line text-4xl"></i>
              </div>
              <h2 className="text-3xl font-bold mb-4">
                Quer implementar IA no seu escritório contábil?
              </h2>
              <p className="text-lg text-white/90 mb-8 leading-relaxed">
                Nossa equipe pode ajudar você a identificar oportunidades de automação, escolher as ferramentas certas e implementar IA de forma responsável e com resultados mensuráveis.
              </p>
              <Link
                to="/contato"
                className="inline-flex items-center gap-3 bg-white text-[#059669] px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-mail-line text-xl"></i>
                Solicite uma Consultoria Gratuita
              </Link>
            </div>
          </div>

          {/* Related Articles */}
          <div className="mt-16 pt-12 border-t-2 border-gray-200">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-8 flex items-center gap-3">
              <i className="ri-article-line text-[#059669]"></i>
              Artigos Relacionados
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Link 
                to="/artigo/como-administrar-escritorio-contabil-sucesso"
                className="group bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-xl transition-all"
              >
                <div className="p-6">
                  <span className="inline-block px-3 py-1 bg-[#1E40AF] text-white text-xs font-semibold rounded mb-3 uppercase tracking-wide">
                    Ativo Fixo &amp; Patrimônio
                  </span>
                  <h3 className="text-lg font-bold text-[#0F172A] mb-2 group-hover:text-[#1E40AF] transition-colors">
                    Como Administrar um Escritório Contábil de Sucesso
                  </h3>
                  <p className="text-sm text-[#64748B] line-clamp-2">
                    Estratégias práticas para gestão eficiente de escritórios contábeis
                  </p>
                </div>
              </Link>
              <Link 
                to="/categoria/tecnologia"
                className="group bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-xl transition-all"
              >
                <div className="p-6">
                  <span className="inline-block px-3 py-1 bg-[#059669] text-white text-xs font-semibold rounded mb-3 uppercase tracking-wide">
                    Categoria
                  </span>
                  <h3 className="text-lg font-bold text-[#0F172A] mb-2 group-hover:text-[#059669] transition-colors">
                    Ver todos os artigos de Tecnologia
                  </h3>
                  <p className="text-sm text-[#64748B] line-clamp-2">
                    Explore mais conteúdos sobre tecnologia aplicada à contabilidade
                  </p>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </>
  );
}
